/**
 * FolderCard · 单个文件夹卡片
 *
 * 视觉：居中文件夹图标 + 名称 + 张数 [+ 子文件夹个数]
 *  · aiGenerated=true → 蓝色调 + 内嵌 AI 水印 sparkle（不再用浮动徽标）
 *  · aiGenerated=false → 黄色调
 *  · subfolderCount > 0 → icon 切到"含内容"样式 + 张数行后面显示"· N 子分类"
 *
 * 交互：
 *  · 普通态：整块可点击 → 跳详情页
 *  · selectMode：左上角覆盖一个 checkbox，点击整块切换勾选；不再跳详情
 */

import React from 'react'
import classNames from 'classnames'
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons'
import type { FolderListItem } from '../../../../types/ai-classify'
import FolderIconSvg from './FolderIconSvg'
import styles from './FolderCard.module.scss'

interface FolderCardProps {
  folder: FolderListItem
  onClick: (folderId: string) => void
  /** 是否是刚创建的 · 加进场动画（短生命周期 ~2s） */
  justCreated?: boolean
  /**
   * 是否显示 NEW 角标 · 新建但用户尚未进入查看的文件夹
   *  · 与 justCreated 区别：justCreated 是动画，showNewBadge 是"未读"标记
   *  · 用户点 folder 进入详情 → 父级 dispatch markFolderEntered 出队
   */
  showNewBadge?: boolean
  /** AI 归档任务进行中 · 卡片底部显示"添加中..."徽标 */
  archiving?: boolean
  /**
   * AI 归档任务已超时 · photoBook 后台扫描已结束但 rule 未匹配到照片。
   * 显示灰色"未匹配到照片"徽标，让用户知道扫描结束（而不是徽标突然消失）。
   * 优先级低于 archiving；同时为 true 时取 archiving。
   */
  archivingTimedOut?: boolean
  /** 多选模式开启 · 卡片左上角显示勾选框，整块点击切换勾选而非跳转 */
  selectMode?: boolean
  /** selectMode 下当前是否被勾选 */
  selected?: boolean
  /**
   * 右键菜单 · antd Dropdown trigger=['contextMenu'] 通过 cloneElement 注入此 prop，
   * 必须透传到根 div 才能让 Dropdown 在卡片上正确响应右键。
   */
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void
  /**
   * checkbox 单独点击 · 与卡片整块 onClick 区分（stopPropagation 阻止冒泡到卡片）
   *  · 非多选态：点 → 父级进入 manageMode + 选中本卡
   *  · 多选态：点 → 父级 toggle 本卡选中态（与整块 onClick 重复，但 checkbox
   *    点击区域更精准，UX 更明确）
   */
  onSelectClick?: (folderId: string) => void
  /**
   * HTML5 拖拽 · 由 FolderList 注入 · 启用后整张卡可拖
   *  · onDragStart 决定拖什么（单卡 / 多选组）
   *  · 其余事件供 FolderList 维护"哪张卡正在被悬停为 drop target"
   * 不传 = 卡片不可拖（非 admin / 不需要时）
   */
  draggable?: boolean
  onDragStart?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDragEnd?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDragOver?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDragLeave?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>, folder: FolderListItem) => void
  /** 当前卡正被拖动 · 视觉降透明度 */
  dragging?: boolean
  /** 当前卡是 drop hover 目标 · 视觉描边高亮 */
  dragOver?: boolean
}

/** 千分位格式化 · 显式 en-US 避免环境差异 */
function formatPhotoCount(count: number): string {
  return count.toLocaleString('en-US')
}

const FolderCard = React.forwardRef<HTMLDivElement, FolderCardProps>(
  (
    {
      folder,
      onClick,
      justCreated,
      showNewBadge,
      archiving,
      archivingTimedOut,
      selectMode,
      selected,
      onContextMenu,
      onSelectClick,
      draggable,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      onDrop,
      dragging,
      dragOver
    },
    ref
  ) => {
    // 虚拟项（待归档）· 银灰配色 + 禁止选中：不渲染勾选框、不挂 data-folder-id
    const isVirtual = folder.virtual === true
    // 智能分类文件夹不再用蓝色区分，而是黄色底 + 右下角刷新水印（isAi 传给图标）
    const isAi = folder.aiGenerated === true
    const variant: 'manual' | 'gray' = isVirtual ? 'gray' : 'manual'
    const hasChildren = folder.subfolderCount > 0
    /**
     * 框选 / 空白菜单识别属性
     *  · 普通卡：data-folder-id 供 BoxSelect 命中检测
     *  · 虚拟项：改挂 data-no-box-select，让起点落在它上面时既不进框选、
     *    也不触发宫格空白右键菜单；且无 data-folder-id 故框选恒不命中
     */
    const itemAttrs = isVirtual
      ? { 'data-no-box-select': true }
      : { 'data-folder-id': folder.folderId }

    return (
      <div
        ref={ref}
        className={classNames(styles.card, {
          [styles.cardJustCreated]: justCreated,
          [styles.cardSelectable]: selectMode,
          [styles.cardSelected]: selectMode && selected,
          [styles.cardDragging]: dragging,
          [styles.cardDragOver]: dragOver
        })}
        onClick={() => onClick(folder.folderId)}
        onContextMenu={onContextMenu}
        draggable={draggable}
        onDragStart={onDragStart ? (e) => onDragStart(e, folder) : undefined}
        onDragEnd={onDragEnd ? (e) => onDragEnd(e, folder) : undefined}
        onDragOver={onDragOver ? (e) => onDragOver(e, folder) : undefined}
        onDragLeave={onDragLeave ? (e) => onDragLeave(e, folder) : undefined}
        onDrop={onDrop ? (e) => onDrop(e, folder) : undefined}
        role="button"
        aria-label={
          selectMode
            ? `${selected ? '取消勾选' : '勾选'} ${folder.folderName}`
            : `打开文件夹 ${folder.folderName}`
        }
        aria-pressed={selectMode ? selected : undefined}
        {...itemAttrs}
      >
        {/* 勾选框 · 总是渲染（占 DOM 位置）
          · selectMode：常亮可见
          · 非 selectMode：默认透明，hover 卡片时淡入
          · 单独 onClick → onSelectClick（不传 = 不响应）；非多选态点击进 manageMode + 选中本卡
          · 虚拟项（待归档）不支持选中 → 整块不渲染勾选框 */}
        {!isVirtual && (
          <span
            className={classNames(styles.selectIndicator, {
              [styles.selectIndicatorChecked]: selected,
              [styles.selectIndicatorAlways]: selectMode
            })}
            onClick={
              onSelectClick
                ? (e) => {
                    e.stopPropagation()
                    onSelectClick(folder.folderId)
                  }
                : undefined
            }
            role="checkbox"
            aria-checked={!!selected}
            aria-label={selected ? '取消勾选' : '勾选'}
          >
            {selected && (
              <CheckOutlined style={{ fontSize: 12, color: '#fff' }} />
            )}
          </span>
        )}
        {/**
         * 右上角徽章 · "添加中" / "NEW" 二选一互斥（同槽位）
         *  · 添加中：归档任务进行中；NEW：归档完成尚未进入
         *  · 优先级：archiving > showNewBadge（添加完成后才会出现 NEW）
         *  · selectMode 下都保留显示，只是和 checkbox 错开位置（checkbox 在左上角） */}
        {archiving ? (
          <span className={styles.archivingBadge} aria-label="照片添加中">
            <LoadingOutlined style={{ marginRight: 4, fontSize: 11 }} />
            添加中
          </span>
        ) : (
          showNewBadge && (
            <span className={styles.newBadge} aria-label="新建文件夹">
              NEW
            </span>
          )
        )}
        <div className={styles.iconWrap}>
          <FolderIconSvg
            size={120}
            variant={variant}
            isAi={isAi}
            hasChildren={hasChildren}
          />
        </div>
        <div className={styles.name} title={folder.folderName}>
          {folder.folderName}
        </div>
        {/* 张数 / 子文件夹数
          · 归档中：底部完全空（"添加中"已移到右上角徽章）· 占位高度交给 .countPlaceholder
          · 归档超时 / 完成 0 张：照常显示"0 张"，不再有"未匹配"特殊徽标
          · archivingTimedOut prop 已无视觉副作用；保留接口供将来加 tooltip 等 */}
        {archiving || isVirtual ? (
          // 待归档虚拟项不展示张数（feat/0614）；归档中同样占位
          <div className={styles.countPlaceholder} aria-hidden />
        ) : (
          <div className={styles.count}>
            {formatPhotoCount(folder.photoCount)} 张
            {hasChildren && (
              <span className={styles.countSep}>
                · {folder.subfolderCount} 子文件夹
              </span>
            )}
          </div>
        )}
      </div>
    )
  }
)

FolderCard.displayName = 'FolderCard'

// React.memo · 框选实时高亮时父组件每帧重渲染，卡片靠 props 浅比较跳过未变动的卡
// （父级传的回调均 useCallback 稳定、folder 引用稳定，只有选中翻转的卡才重渲染）
export default React.memo(FolderCard)
